/**
 * Mensagens de validação em português, sem nada técnico (nem inglês, nem regex, nem tipo de
 * dado). Os schemas com mensagem própria continuam com ela; este é o padrão para o que não
 * tem, e vale para a tela e para a API (as duas importam este pacote).
 *
 * O nome do campo entra na mensagem geral pela `formatValidationIssues` (API) — embaixo de
 * cada campo a tela mostra só a mensagem, sem repetir o nome.
 */
import { z } from "zod";

type RawIssue = {
  code?: string;
  input?: unknown;
  expected?: string;
  origin?: string;
  format?: string;
  minimum?: number | bigint;
  maximum?: number | bigint;
  inclusive?: boolean;
};

const num = (v: number | bigint | undefined) =>
  v === undefined ? "" : String(v).replace(".", ",");

/** Mensagem padrão para um problema sem mensagem própria. */
export function defaultValidationMessage(issue: RawIssue): string {
  switch (issue.code) {
    case "invalid_type":
      return issue.input === undefined || issue.input === null || issue.input === ""
        ? "Campo obrigatório."
        : "Valor inválido.";
    case "too_small":
      if (issue.origin === "string") {
        return Number(issue.minimum) <= 1
          ? "Campo obrigatório."
          : `Use pelo menos ${num(issue.minimum)} caracteres.`;
      }
      if (issue.origin === "array" || issue.origin === "set") {
        return `Inclua pelo menos ${num(issue.minimum)} item(ns).`;
      }
      return issue.inclusive === false
        ? `O valor precisa ser maior que ${num(issue.minimum)}.`
        : `O valor mínimo é ${num(issue.minimum)}.`;
    case "too_big":
      if (issue.origin === "string") return `Use no máximo ${num(issue.maximum)} caracteres.`;
      if (issue.origin === "array" || issue.origin === "set") {
        return `Inclua no máximo ${num(issue.maximum)} itens.`;
      }
      return issue.inclusive === false
        ? `O valor precisa ser menor que ${num(issue.maximum)}.`
        : `O valor máximo é ${num(issue.maximum)}.`;
    case "invalid_format":
      if (issue.format === "email") return "E-mail inválido.";
      if (issue.format === "uuid" || issue.format === "guid") return "Escolha uma opção válida.";
      if (issue.format === "date" || issue.format === "datetime" || issue.format === "time") {
        return "Data inválida.";
      }
      return "Formato inválido.";
    case "invalid_value":
      return "Escolha uma das opções da lista.";
    case "unrecognized_keys":
      return "Há um campo que não faz parte deste formulário.";
    default:
      return "Valor inválido.";
  }
}

// configuração única do Zod deste pacote (os schemas compartilhados usam esta instância)
z.config({ customError: (issue) => defaultValidationMessage(issue as RawIssue) });

/** As mensagens acima são genéricas: na mensagem geral recebem o nome do campo na frente. */
const GENERIC =
  /^(Campo obrigatório|Valor inválido|Use pelo menos|Use no máximo|Inclua pelo menos|Inclua no máximo|O valor mínimo|O valor máximo|O valor precisa|E-mail inválido|Escolha uma opção válida|Data inválida|Formato inválido|Escolha uma das opções da lista|Há um campo que não)/;

/** Nome que a pessoa vê, pelo nome do campo no formulário. */
export const FIELD_LABELS: Record<string, string> = {
  code: "Código",
  name: "Nome",
  kind: "Tipo",
  legalName: "Razão social",
  tradeName: "Nome fantasia",
  organizationName: "Nome da organização",
  document: "CPF/CNPJ",
  cnpj: "CNPJ",
  stateRegistration: "Inscrição estadual",
  municipalRegistration: "Inscrição municipal",
  ieIndicator: "Indicador de IE",
  email: "E-mail",
  phone: "Telefone",
  mobile: "Celular",
  cep: "CEP",
  street: "Endereço",
  number: "Número",
  complement: "Complemento",
  district: "Bairro",
  city: "Cidade",
  cityIbgeCode: "Código IBGE do município",
  uf: "UF",
  isCustomer: "Cliente",
  isSupplier: "Fornecedor",
  creditLimit: "Limite de crédito",
  salespersonId: "Vendedor",
  priceListId: "Tabela de preço",
  paymentMethodId: "Forma de pagamento",
  notes: "Observações",
  status: "Situação",
  finalConsumer: "Consumidor final",
  ruralProducer: "Produtor rural",
  description: "Descrição",
  ncm: "NCM",
  cest: "CEST",
  origin: "Origem da mercadoria",
  gtin: "GTIN",
  exTipi: "EX TIPI",
  taxGroupId: "Grupo tributário",
  baseUnitId: "Unidade-base",
  unitId: "Unidade",
  factor: "Fator",
  decimals: "Casas decimais",
  netWeightKg: "Peso líquido",
  grossWeightKg: "Peso bruto",
  shelfLifeDays: "Validade (dias)",
  customerId: "Cliente",
  supplierId: "Fornecedor",
  productId: "Produto",
  warehouseId: "Depósito",
  originWarehouseId: "Depósito de origem",
  destinationWarehouseId: "Depósito de destino",
  outputWarehouseId: "Depósito de saída",
  quantity: "Quantidade",
  plannedQuantity: "Quantidade planejada",
  unitPrice: "Preço unitário",
  price: "Preço",
  maxDiscountPercent: "Desconto máximo",
  discount: "Desconto",
  freight: "Frete",
  insurance: "Seguro",
  other: "Outras despesas",
  amount: "Valor",
  dueDate: "Vencimento",
  orderDate: "Data do pedido",
  entryDate: "Data de entrada",
  issueDate: "Data de emissão",
  productionDate: "Data da produção",
  validFrom: "Início da vigência",
  validTo: "Fim da vigência",
  validUntil: "Validade",
  reason: "Motivo",
  installments: "Parcelas",
  items: "Itens",
  lotCode: "Lote",
  lotExpiresOn: "Validade do lote",
  expiresOn: "Validade",
  environment: "Ambiente",
  provider: "Provedor",
  model: "Modelo",
  series: "Série",
  nextNumber: "Próximo número",
  cfop: "CFOP",
  cst: "CST",
  csosn: "CSOSN",
  modBC: "Modalidade da base",
  ufDest: "UF de destino",
  ufOrigin: "UF de origem",
  destScope: "Operação interna/interestadual",
  personKind: "Tipo de pessoa",
  operationId: "Operação fiscal",
  nfceMaxAmountUnidentified: "Valor máximo da NFC-e sem identificar o comprador",
  nfceCancelMinutes: "Prazo de cancelamento da NFC-e",
  nfeCancelHours: "Prazo de cancelamento da NF-e",
  rate: "Alíquota",
  tax: "Tributo",
  cardBrand: "Bandeira",
  authorizationCode: "Autorização",
  nsu: "NSU",
  pixTxid: "TxId",
  pixEndToEnd: "EndToEndId",
  terminalId: "Terminal",
  integrationType: "Integração",
  institutionId: "Instituição",
  invoiceNumber: "Número da nota",
  invoiceSeries: "Série da nota",
  invoiceModel: "Modelo da nota",
  invoiceTotal: "Valor total da nota",
  conversion: "Conversão",
};

type IssueLike = { path: readonly PropertyKey[]; message: string };

/** "Itens (linha 2) — Quantidade" a partir do caminho do campo, ou null se não houver nome. */
export function fieldLabelOf(path: readonly PropertyKey[]): string | null {
  const names = path.filter((p): p is string => typeof p === "string");
  const last = names[names.length - 1];
  const label = last ? FIELD_LABELS[last] : undefined;
  if (!label) return null;
  const index = path.find((p): p is number => typeof p === "number");
  return index === undefined ? label : `${label} (linha ${index + 1})`;
}

/**
 * Mensagem geral de uma validação que falhou: o primeiro problema, com o nome do campo
 * quando a mensagem é genérica, e quantos campos mais há para revisar.
 */
export function formatValidationIssues(issues: readonly IssueLike[]): string {
  const first = issues[0];
  if (!first) return "Dados inválidos. Revise o formulário.";
  const label = fieldLabelOf(first.path);
  let message = first.message;
  if (GENERIC.test(message)) {
    message = label
      ? `${label}: ${message.charAt(0).toLowerCase()}${message.slice(1)}`
      : `${message} Revise os campos destacados.`;
  }
  const more = new Set(issues.slice(1).map((i) => i.path.join("."))).size;
  return more > 0 ? `${message} (e mais ${more} campo(s) para revisar)` : message;
}

/** Parece mensagem técnica (inglês, regex, tipos)? A tela troca por uma mensagem funcional. */
export function looksTechnical(message: string): boolean {
  return /\b(Invalid|expected|received|must match|pattern|Too (small|big)|undefined|null)\b|\/\^|\$\//.test(
    message,
  );
}
